import { createSelector, Selector } from '@reduxjs/toolkit'
import { GlobalState } from 'store'
import { gridStoreSelector } from './storeSelectors'
import { gridDefault, GridId, GridSettings, GridSettingsId } from 'store/grid'
import { createWindowSelectionsSelector } from 'selectors/selectionSelectors'
import { WindowSelections } from 'store/selection/selectionTypes'
import { BestPriceType, GlobalGridSettings } from '../store/grid/gridTypes'
import { isCurrentOrganisationBuySideSelector, isCurrentOrganisationSellSideSelector } from './organisationSelectors/organisationSelectors'
import { isAdminGatewayUserSelector, isTradingGatewayUserSelector } from './userSelectors'
import { globalGridSettingsSelector } from './globalGridSelectors'
import { ParametricSelector } from 'utils/redux'

export function createGridSettingsListSelector<TProps>(
  gridIdSelector: ParametricSelector<{}, TProps, GridId>
): ParametricSelector<GlobalState, TProps, GridSettings[]> {
  return createSelector(gridStoreSelector, gridIdSelector, (gridStore, gridId) => Object.values(gridStore[gridId]))
}

export function createSelectedGridSettingsSelector<TProps>(
  gridIdSelector: ParametricSelector<{}, TProps, GridId>,
  gridSettingsIdSelector: ParametricSelector<{}, TProps, GridSettingsId>
): ParametricSelector<GlobalState, TProps, GridSettings> {
  return createSelector([gridStoreSelector, gridIdSelector, gridSettingsIdSelector], (gridStore, gridId, gridSettingsId) => {
    return gridStore[gridId] ? gridStore[gridId][gridSettingsId] : (gridDefault.default as GridSettings)
  })
}

export const showOrgActivityToggleSelector: Selector<GlobalState, boolean> = createSelector(
  isCurrentOrganisationBuySideSelector,
  isTradingGatewayUserSelector,
  (buySideUser: boolean, tradingGatewayUser: boolean) => {
    return buySideUser && tradingGatewayUser
  }
)

export const showSalesCoverToggleSelector: Selector<GlobalState, boolean> = createSelector(
  isCurrentOrganisationSellSideSelector,
  isTradingGatewayUserSelector,
  (sellSideUser: boolean, tradingGatewayUser: boolean) => {
    return sellSideUser && tradingGatewayUser
  }
)

export const showOrgActivitySelector: Selector<GlobalState, boolean> = createSelector(
  globalGridSettingsSelector,
  isCurrentOrganisationBuySideSelector,
  isAdminGatewayUserSelector,
  (globalGridSettings: GlobalGridSettings, buySideUser: boolean, adminUser: boolean) => {
    return (globalGridSettings && globalGridSettings.showOrgActivity) || adminUser || !buySideUser
  }
)

export const userGlobalGridSettingsSelector: Selector<GlobalState, GlobalGridSettings> = createSelector(
  globalGridSettingsSelector,
  showOrgActivitySelector,
  (globalGridSettings, showOrgActivity) => ({ ...globalGridSettings, showOrgActivity })
)

export const bestPriceTypeSelector: Selector<GlobalState, BestPriceType> = createSelector(
  globalGridSettingsSelector,
  (globalGridSettings: GlobalGridSettings) => globalGridSettings.bestPriceType || BestPriceType.REPO_INTEREST
)

export const showOnlyUsersSalesActivitySelector: Selector<GlobalState, boolean> = createSelector(
  globalGridSettingsSelector,
  globalGridSettings => globalGridSettings.showOnlyUsersSalesActivity
)

export const showMaturedCollateralsSelector: Selector<GlobalState, boolean> = createSelector(
  globalGridSettingsSelector,
  globalGridSettings => globalGridSettings.showMaturedCollaterals
)

export const hideExpiredRejectedSelector: Selector<GlobalState, boolean> = createSelector(
  globalGridSettingsSelector,
  globalGridSettings => globalGridSettings.hideExpiredRejectedInInitiated
)

export const allGridSettingsSelector: Selector<GlobalState, GlobalGridSettings> = createSelector(
  globalGridSettingsSelector,
  globalGridSettings => globalGridSettings
)

export function createSelectedGridSettingsIdSelector<TProps>(gridIdSelector: ParametricSelector<{}, TProps, GridId>) {
  return createSelector(createWindowSelectionsSelector(), gridIdSelector, (selectionStore: WindowSelections, gridId: GridId) => {
    return selectionStore.selectedGridSettings[gridId]
  })
}

export const gridColumnCopySelectionsByGridIdSelector = (gridIdSelector: ParametricSelector<unknown, unknown, GridId>) =>
  createSelector([createSelectedGridSettingsSelector(gridIdSelector, () => 'default')], settings => settings.columnCopySelections ?? [])

export const createBulkDateUpdateValuesByGridIdSelector = <TProps>(gridIdSelector: ParametricSelector<{}, TProps, GridId>) =>
  createSelector(
    [createSelectedGridSettingsSelector(gridIdSelector, () => 'default')],
    settings => settings.bulkDateUpdateFields ?? { startDate: null, endDate: null, term: null }
  )
